import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { platformApi } from './platformApi.js';
import { Badge } from '@/components/ui/Badge.jsx';
import { Button } from '@/components/ui/Button.jsx';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card.jsx';
import { Dialog } from '@/components/ui/Dialog.jsx';
import { Input } from '@/components/ui/Input.jsx';
import { ArrowLeft, Send, Rocket } from 'lucide-react';

const STATUS_VARIANT = { active: 'success', provisioning: 'info', suspended: 'warning', archived: 'default', 'provisioning-failed': 'danger' };
const JOB_VARIANT = { queued: 'info', running: 'warning', succeeded: 'success', failed: 'danger' };

const LIFECYCLE_ACTIONS = {
  provisioning: [{ label: 'Activate', status: 'active', variant: 'success' }],
  active: [{ label: 'Suspend', status: 'suspended', variant: 'destructive' }],
  suspended: [
    { label: 'Resume', status: 'active', variant: 'success' },
    { label: 'Archive', status: 'archived', variant: 'destructive' },
  ],
  'provisioning-failed': [{ label: 'Archive', status: 'archived', variant: 'destructive' }],
  archived: [],
};

export default function CustomerDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteHours, setInviteHours] = useState('72');
  const [inviteResult, setInviteResult] = useState(null);
  const [jobForm, setJobForm] = useState({ operation: 'reconcile', profileId: '', coreVersion: '', idempotencyKey: '' });
  const [jobOpen, setJobOpen] = useState(false);
  const [jobError, setJobError] = useState('');
  const [deploymentOpen, setDeploymentOpen] = useState(false);
  const [deploymentError, setDeploymentError] = useState('');
  const [deploymentForm, setDeploymentForm] = useState(null);
  const [deploymentConfirmed, setDeploymentConfirmed] = useState(false);

  const { data: customer, isLoading } = useQuery({
    queryKey: ['platform', 'customers', id],
    queryFn: () => platformApi.getCustomer(id),
  });

  const { data: jobsData } = useQuery({
    queryKey: ['platform', 'jobs', id],
    queryFn: () => platformApi.listJobs(id),
  });

  const lifecycle = useMutation({
    mutationFn: ({ status }) => platformApi.setLifecycle(id, status, 'platform-ui'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform', 'customers', id] }),
  });

  const createInvite = useMutation({
    mutationFn: () => platformApi.createInvitation(id, Number(inviteHours) || 72),
    onSuccess: (data) => setInviteResult(data),
  });

  const createJob = useMutation({
    mutationFn: (body) => platformApi.createJob(body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['platform', 'jobs', id] }); setJobOpen(false); },
    onError: (e) => setJobError(e.message),
  });

  const updateDeployment = useMutation({
    mutationFn: (body) => platformApi.updateDeployment(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['platform', 'customers', id] });
      setDeploymentOpen(false);
      setDeploymentConfirmed(false);
    },
    onError: (e) => setDeploymentError(e.message),
  });

  if (isLoading) return <p className="text-sm text-slate-500">Loading…</p>;
  if (!customer) return <p className="text-sm text-red-600">Customer not found</p>;

  const actions = LIFECYCLE_ACTIONS[customer.lifecycle_status] ?? [];
  const deploymentStatus = customer.deployment_status ?? 'not recorded';
  const deploymentVariant = { pending: 'warning', ready: 'success', disabled: 'default' }[customer.deployment_status] ?? 'default';
  const canDeploy = customer.lifecycle_status !== 'archived';
  let desiredExtensionVersions = customer.desired_extensions_json ?? '{}';
  try { desiredExtensionVersions = JSON.stringify(JSON.parse(desiredExtensionVersions), null, 2); } catch { desiredExtensionVersions = 'invalid metadata'; }
  const desiredExtensionEvidence = customer.desired_extension_evidence_sha256 ?? null;
  const actualExtensionEvidence = customer.actual_extension_evidence_sha256 ?? null;
  const extensionEvidenceState = !desiredExtensionEvidence
    ? { label: 'Core-only', variant: 'default' }
    : !actualExtensionEvidence
      ? { label: 'Pending deployment evidence', variant: 'warning' }
      : desiredExtensionEvidence === actualExtensionEvidence
        ? { label: 'Matched', variant: 'success' }
        : { label: 'Mismatch', variant: 'danger' };

  function openDeploymentDialog() {
    setDeploymentError('');
    setDeploymentConfirmed(false);
    setDeploymentForm({
      targetBinding: customer.target_binding ?? `CUSTOMER_${id.replaceAll('-', '_').toUpperCase()}`,
      configuredCustomerId: id,
      deploymentRevision: customer.deployment_revision ?? '',
      databaseIdentity: customer.database_identity ?? '',
      deploymentStatus: customer.deployment_status === 'disabled' ? 'pending' : 'ready',
    });
    setDeploymentOpen(true);
  }

  return (
    <div className="space-y-5 max-w-3xl">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/platform/customers')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-lg font-semibold text-slate-900 dark:text-slate-100">{customer.id}</h1>
        <Badge variant={STATUS_VARIANT[customer.lifecycle_status] ?? 'default'}>{customer.lifecycle_status}</Badge>
      </div>

      <Card>
        <CardHeader><CardTitle>Customer</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 text-sm">
            {[
              ['Company', customer.company_name ?? '—'],
              ['Administrator', customer.administrator_name ?? '—'],
              ['Administrator email', customer.administrator_email ?? '—'],
              ['Managed hostname', customer.host ?? '—'],
            ].map(([label, value]) => (
              <div key={label}><span className="text-slate-500">{label}</span><p className="font-medium text-slate-900 dark:text-slate-100 break-all">{value}</p></div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-3">
            <div>
              <CardTitle>Deployment</CardTitle>
              <p className="mt-1 text-sm text-slate-500">Isolated customer Worker identity and routing state.</p>
            </div>
            {canDeploy && <Button size="sm" onClick={openDeploymentDialog}><Rocket className="h-4 w-4" /> {customer.deployment_status ? 'Redeploy' : 'Record deployment'}</Button>}
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 text-sm">
            {[
              ['Profile / release', customer.profile_id ?? '—'],
              ['Binding', customer.target_binding ?? '—'],
              ['Database identity', customer.database_identity ?? '—'],
              ['Revision', customer.deployment_revision ?? '—'],
              ['Managed hostname', customer.host ?? '—'],
              ['Hostname status', customer.hostname_status ?? '—'],
            ].map(([label, value]) => (
              <div key={label}>
                <span className="text-slate-500">{label}</span>
                <p className="font-medium text-slate-900 dark:text-slate-100 break-all">{value}</p>
              </div>
            ))}
            <div><span className="text-slate-500">Readiness</span><p className="mt-1"><Badge variant={deploymentVariant}>{deploymentStatus}</Badge></p></div>
            <div><span className="text-slate-500">Health check</span><p className="font-medium text-slate-900 dark:text-slate-100">{customer.deployment_status === 'ready' ? 'Ready state recorded' : 'Not verified'}</p></div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Deployment profile metadata</CardTitle>
          <p className="mt-1 text-sm text-slate-500">Build-selected extension identity and reconciliation evidence. This does not represent application feature validation.</p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div><span className="text-slate-500">Extension evidence state</span><p className="mt-1"><Badge variant={extensionEvidenceState.variant}>{extensionEvidenceState.label}</Badge></p></div>
            <div><span className="text-slate-500">Desired extension versions</span><pre className="mt-1 whitespace-pre-wrap break-all font-mono text-xs text-slate-900 dark:text-slate-100">{desiredExtensionVersions}</pre></div>
            <div><span className="text-slate-500">Desired evidence hash</span><p className="font-mono text-xs break-all text-slate-900 dark:text-slate-100">{desiredExtensionEvidence ?? '—'}</p></div>
            <div><span className="text-slate-500">Actual evidence hash</span><p className="font-mono text-xs break-all text-slate-900 dark:text-slate-100">{actualExtensionEvidence ?? '—'}</p></div>
            <div><span className="text-slate-500">Actual release</span><p className="font-mono text-xs break-all text-slate-900 dark:text-slate-100">{customer.actual_release_id ?? '—'}</p></div>
            <div><span className="text-slate-500">Gateway release</span><p className="font-mono text-xs break-all text-slate-900 dark:text-slate-100">{customer.gateway_release_id ?? '—'}</p></div>
            <div><span className="text-slate-500">Gateway revision</span><p className="font-mono text-xs break-all text-slate-900 dark:text-slate-100">{customer.gateway_revision ?? '—'}</p></div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 gap-4 text-sm">
        {[
          ['Lifecycle revision', customer.lifecycle_revision],
          ['Deployment status', customer.deployment_status ?? '—'],
          ['Deployment revision', customer.deployment_revision ?? '—'],
          ['Profile', customer.profile_id ?? '—'],
          ['Desired core version', customer.desired_core_version ?? '—'],
          ['Actual core version', customer.actual_core_version ?? '—'],
          ['Last error', customer.last_error_code ?? '—'],
          ['Last reconciled', customer.last_reconciled_at ? new Date(customer.last_reconciled_at).toLocaleString() : '—'],
        ].map(([label, value]) => (
          <div key={label}>
            <span className="text-slate-500">{label}</span>
            <p className="font-medium text-slate-900 dark:text-slate-100">{value}</p>
          </div>
        ))}
      </div>

      {(actions.length > 0 || customer.lifecycle_status !== 'archived') && (
        <div className="flex flex-wrap gap-2">
          {actions.map(({ label, status, variant }) => (
            <Button key={status} variant={variant} size="sm" disabled={lifecycle.isPending} onClick={() => lifecycle.mutate({ status })}>
              {label}
            </Button>
          ))}
          {customer.lifecycle_status !== 'archived' && (
            <>
              <Button variant="outline" size="sm" onClick={() => { setInviteResult(null); setInviteOpen(true); }}>
                <Send className="h-4 w-4" /> Create Invitation
              </Button>
              <details className="basis-full">
                <summary className="cursor-pointer text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300">Advanced provisioning controls</summary>
                <div className="mt-2"><Button variant="outline" size="sm" onClick={() => { setJobError(''); setJobOpen(true); }}>Queue Provisioning Job</Button></div>
              </details>
            </>
          )}
        </div>
      )}

      <Card>
        <CardHeader><CardTitle>Provisioning Jobs</CardTitle></CardHeader>
        <CardContent>
          {(jobsData?.jobs ?? []).length === 0
            ? <p className="text-sm text-slate-500">No jobs yet</p>
            : (
              <div className="space-y-2">
                {(jobsData?.jobs ?? []).map((job) => (
                  <div key={job.id} className="flex items-center justify-between text-sm border-b border-slate-100 dark:border-slate-800 pb-2 last:border-0 last:pb-0">
                    <div>
                      <span className="font-mono text-xs text-slate-500">{job.id}</span>
                      <span className="ml-2 text-slate-700 dark:text-slate-300">{job.operation}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {job.error_code && <span className="text-xs text-red-600">{job.error_code}</span>}
                      <Badge variant={JOB_VARIANT[job.status] ?? 'default'}>{job.status}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
        </CardContent>
      </Card>

      <Dialog open={deploymentOpen} onOpenChange={setDeploymentOpen} title="Deploy customer Worker">
        {deploymentForm && <form className="p-4 space-y-3" onSubmit={(e) => { e.preventDefault(); setDeploymentError(''); updateDeployment.mutate({ ...deploymentForm, actorId: 'platform-ui' }); }}>
          <p className="text-sm text-slate-600 dark:text-slate-300">Use this only after the isolated Worker has been deployed and its health check has passed.</p>
          {[
            { name: 'targetBinding', label: 'Binding' },
            { name: 'configuredCustomerId', label: 'Configured customer ID' },
            { name: 'deploymentRevision', label: 'Deployment revision' },
            { name: 'databaseIdentity', label: 'Database identity' },
          ].map(({ name, label }) => <div key={name} className="space-y-1"><label className="text-sm font-medium text-slate-700 dark:text-slate-300">{label}</label><Input value={deploymentForm[name]} onChange={(e) => setDeploymentForm((f) => ({ ...f, [name]: e.target.value }))} required /></div>)}
          <div className="space-y-1"><label className="text-sm font-medium text-slate-700 dark:text-slate-300">Readiness</label><select className="h-9 w-full rounded-md border border-slate-300 bg-white px-3 text-sm dark:bg-slate-900 dark:border-slate-700" value={deploymentForm.deploymentStatus} onChange={(e) => setDeploymentForm((f) => ({ ...f, deploymentStatus: e.target.value }))}><option value="pending">Pending</option><option value="ready">Ready</option><option value="disabled">Disabled</option></select></div>
          <label className="flex items-start gap-2 text-sm text-slate-600 dark:text-slate-300"><input type="checkbox" checked={deploymentConfirmed} onChange={(e) => setDeploymentConfirmed(e.target.checked)} className="mt-1" /> I confirm these values match the deployed isolated Worker.</label>
          {deploymentError && <p className="text-sm text-red-600">{deploymentError}</p>}
          <div className="flex justify-end gap-2"><Button type="button" variant="outline" onClick={() => setDeploymentOpen(false)}>Cancel</Button><Button type="submit" disabled={!deploymentConfirmed || updateDeployment.isPending}>{updateDeployment.isPending ? 'Saving…' : 'Save deployment state'}</Button></div>
        </form>}
      </Dialog>

      {/* Invitation dialog */}
      <Dialog open={inviteOpen} onOpenChange={(v) => { setInviteOpen(v); if (!v) setInviteResult(null); }} title="Create Admin Invitation">
        <div className="p-4 space-y-3">
          {inviteResult ? (
            <div className="space-y-3">
              <p className="text-sm text-slate-700 dark:text-slate-300">
                Copy this token and deliver it out-of-band. It will not be shown again.
              </p>
              <div className="bg-slate-100 dark:bg-slate-800 rounded p-3 font-mono text-xs break-all select-all">
                {inviteResult.token}
              </div>
              <p className="text-xs text-slate-500">Expires: {new Date(inviteResult.expiresAt).toLocaleString()}</p>
              <Button className="w-full" onClick={() => { setInviteOpen(false); setInviteResult(null); }}>Done</Button>
            </div>
          ) : (
            <>
              <div className="space-y-1">
                <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Expires in (hours)</label>
                <Input type="number" min="1" max="168" value={inviteHours} onChange={(e) => setInviteHours(e.target.value)} />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setInviteOpen(false)}>Cancel</Button>
                <Button disabled={createInvite.isPending} onClick={() => createInvite.mutate()}>
                  {createInvite.isPending ? 'Creating…' : 'Generate Token'}
                </Button>
              </div>
            </>
          )}
        </div>
      </Dialog>

      {/* Provisioning job dialog */}
      <Dialog open={jobOpen} onOpenChange={setJobOpen} title="Queue Provisioning Job">
        <form className="p-4 space-y-3" onSubmit={(e) => { e.preventDefault(); setJobError(''); createJob.mutate({ ...jobForm, customerId: id }); }}>
          {[
            { name: 'operation', label: 'Operation', placeholder: 'create / reconcile / suspend / resume' },
            { name: 'profileId', label: 'Profile ID', placeholder: 'standard' },
            { name: 'coreVersion', label: 'Core Version', placeholder: '2026.10.0' },
            { name: 'idempotencyKey', label: 'Idempotency Key', placeholder: 'unique-key' },
          ].map(({ name, label, placeholder }) => (
            <div key={name} className="space-y-1">
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300">{label}</label>
              <Input value={jobForm[name]} onChange={(e) => setJobForm((f) => ({ ...f, [name]: e.target.value }))} placeholder={placeholder} required />
            </div>
          ))}
          {jobError && <p className="text-sm text-red-600">{jobError}</p>}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setJobOpen(false)}>Cancel</Button>
            <Button type="submit" disabled={createJob.isPending}>{createJob.isPending ? 'Queuing…' : 'Queue Job'}</Button>
          </div>
        </form>
      </Dialog>
    </div>
  );
}
